import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '../common/Alert.js';
import shipmentClientService from '../../services/ShipmentClientService.js';
import { Users, Plus, Eye, Pencil, Trash2, FileText, Send, DollarSign } from 'lucide-react';
import {
  PageLayout,
  Button,
  DataTable,
  FilterPanel,
  Pagination,
  StatusBadge,
} from '../common/index.js';

const ShipmentClientList = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterValues, setFilterValues] = useState({
    search: '',
    status: '',
    company_name: '',
  });
  const navigate = useNavigate();
  const perPage = 10;

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchClients(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [filterValues]);

  useEffect(() => {
    fetchClients(currentPage);
  }, [currentPage]);

  const fetchClients = async (page = currentPage) => {
    try {
      setLoading(true);
      const filters = {};
      if (filterValues.search) filters.search = filterValues.search;
      if (filterValues.status) filters.status = filterValues.status;
      if (filterValues.company_name) filters.company_name = filterValues.company_name;

      const response = await shipmentClientService.getAll(page, perPage, filters);
      setClients(response.data);
      setTotalPages(response.pagination.pages);
    } catch (err) {
      Alert.error('Failed to load shipment clients');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (clientId, clientName) => {
    const confirmed = await Alert.confirm(
      `Are you sure you want to delete "${clientName}"? This action cannot be undone.`,
      'Delete Shipment Client'
    );

    if (!confirmed) return;

    try {
      await shipmentClientService.delete(clientId);
      Alert.success('Shipment client deleted successfully!');

      if (clients.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchClients();
      }
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to delete shipment client');
    }
  };

  const handleDownloadInvoice = async (clientId, clientName) => {
    try {
      await shipmentClientService.downloadClientInvoice(clientId);
      Alert.success(`Invoice downloaded for ${clientName}`);
    } catch (err) {
      Alert.error('Failed to download invoice');
      console.error(err);
    }
  };

  const handleSendInvoice = async (clientId, clientName) => {
    const confirmed = await Alert.confirm(
      `Send invoice to ${clientName}'s email?`,
      'Send Invoice'
    );

    if (!confirmed) return;

    try {
      await shipmentClientService.sendClientInvoice(clientId);
      Alert.success(`Invoice sent successfully to ${clientName}`);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to send invoice');
    }
  };

  const columns = [
    {
      key: 'full_name',
      label: 'Full Name',
      render: (client) => (
        <button
          onClick={() => navigate(`/shipment-clients/view/${client.id}`)}
          className="font-medium text-brand hover:underline text-left"
        >
          {client.full_name}
        </button>
      ),
    },
    { key: 'email', label: 'Email' },
    { key: 'contact', label: 'Contact' },
    { key: 'company_name', label: 'Company' },
    { key: 'address', label: 'Address', cellClassName: 'max-w-[200px] truncate' },
    {
      key: 'status',
      label: 'Status',
      render: (client) => <StatusBadge status={client.status} />,
    },
    { key: 'created_by', label: 'Created By' },
  ];

  const getActions = (client) => [
    {
      label: 'View Details',
      icon: Eye,
      onClick: () => navigate(`/shipment-clients/view/${client.id}`),
    },
    {
      label: 'Batch Payment',
      icon: DollarSign,
      onClick: () => navigate(`/shipment-clients/batch-bill/${client.id}`),
    },
    {
      label: 'Edit',
      icon: Pencil,
      onClick: () => navigate(`/shipment-clients/edit/${client.id}`),
    },
    {
      label: 'Download Invoice',
      icon: FileText,
      onClick: () => handleDownloadInvoice(client.id, client.full_name),
    },
    {
      label: 'Email Invoice',
      icon: Send,
      onClick: () => handleSendInvoice(client.id, client.full_name),
    },
    {
      label: 'Delete',
      icon: Trash2,
      variant: 'danger',
      onClick: () => handleDelete(client.id, client.full_name),
    },
  ];

  return (
    <PageLayout
      title="Clients & Vendors"
      icon={Users}
      actions={
        <Button icon={Plus} onClick={() => navigate('/shipment-clients/add')}>
          Add Client / Vendor
        </Button>
      }
    >
      <FilterPanel
        filters={[
          { key: 'search', label: 'Search', placeholder: 'Name, email, contact...' },
          {
            key: 'status',
            label: 'Status',
            type: 'select',
            options: [
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ],
          },
          { key: 'company_name', label: 'Company', placeholder: 'Company name...' },
        ]}
        values={filterValues}
        onChange={setFilterValues}
        onClear={() => setFilterValues({ search: '', status: '', company_name: '' })}
      />

      <DataTable
        columns={columns}
        data={clients}
        loading={loading}
        emptyMessage="No shipment clients found"
        rowKey="id"
        actions={getActions}
      />

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </PageLayout>
  );
};

export default ShipmentClientList;
